import { AxiosRequestConfig } from 'axios';
import {
  CalendarCategory,
  CalendarEvent,
  CalendarEventForm,
  CalendarEventStatus,
  CalendarSettings,
  CalendarSettingsForm,
  CreateUserTimeOff,
  UserTimeOff
} from 'types/calendar';
import { CustomField } from 'types/company-settings';
import axiosInstance from 'utils/axios';

interface CalendarQuery {
  month?: string;
  year?: string;
  day?: string;
  startDate?: string;
  endDate?: string;
  keyword?: string;
  // When false, the API skips the connected-calendar (Google/Outlook) fetch.
  showThirdPartyEvents?: boolean;
}

export async function getCalendarEventsByUserId(userId: string, query?: CalendarQuery): Promise<CalendarEvent[]> {
  try {
    const params = {
      ...query,
      userId
    };

    const { data } = await axiosInstance.get<CalendarEvent[]>('/api/calendar/list', { params });

    return data;
  } catch (error) {
    console.error(error);
    throw new Error('Something went wrong while retrieving events!');
  }
}

export async function searchCalendarEvents(userId: string, keyword: string): Promise<CalendarEvent[]> {
  return getCalendarEventsByUserId(userId, { keyword });
}

export async function getCalendarEventById(id: string): Promise<CalendarEvent> {
  try {
    const { data } = await axiosInstance.get<CalendarEvent>(`/api/calendar/${id}`);
    return data;
  } catch (error) {
    console.error(error);
    throw new Error('Something went wrong while retrieving the event!');
  }
}

export async function getCalendarByParticipant(participantId: string): Promise<CalendarEvent[]> {
  try {
    const { data } = await axiosInstance.get<CalendarEvent[]>(`/api/calendar/list-by-participant/${participantId}`);

    return data;
  } catch (error) {
    console.error(error);
    throw new Error('Something went wrong while retrieving events!');
  }
}

// Wrap an axios error keeping a friendly message AND the original response, so
// callers can both show messages[0] and detect a 409 overlap (data.conflicts).
function wrapCalendarError(error: any, fallback: string): Error {
  const wrapped: any = new Error(error?.response?.data?.messages?.[0] ?? error?.message ?? fallback);
  wrapped.response = error?.response;
  return wrapped;
}

export async function createCalendarEvent(data: CalendarEventForm) {
  try {
    await axiosInstance.post('/api/calendar', data);
  } catch (error: any) {
    console.error(error);
    throw wrapCalendarError(error, 'Something went wrong while creating event!');
  }
}

export async function updateCalendarEventById(id: string, data: CalendarEventForm, scope?: 'this' | 'all') {
  try {
    await axiosInstance.put(`/api/calendar/${id}${scope ? `?scope=${scope}` : ''}`, data);
  } catch (error: any) {
    console.error(error);
    throw wrapCalendarError(error, 'Something went wrong while updating event!');
  }
}

export async function deleteCalendarEventById(id: string, scope?: 'this' | 'all') {
  try {
    await axiosInstance.delete(`/api/calendar/${id}${scope ? `?scope=${scope}` : ''}`);
  } catch (error: any) {
    console.error(error);
    throw wrapCalendarError(error, 'Something went wrong while deleting event!');
  }
}

export async function updateCalendarEventStatus(id: string, status: CalendarEventStatus) {
  try {
    await axiosInstance.patch(`/api/calendar/${id}/status`, { status });
  } catch (error) {
    console.error(error);
    throw new Error('Something went wrong while updating event status!');
  }
}

export async function getCalendarSettingsByUserId(userId: string): Promise<CalendarSettings> {
  try {
    const { data } = await axiosInstance.get<CalendarSettings>(`/api/user/${userId}/calendar-settings`);
    return data;
  } catch (error) {
    console.error(error);
    throw new Error('Something went wrong while retrieving Calendar settings!');
  }
}

/** The server's own reason for a rejected save (e.g. an invalid application link). */
const apiErrorMessage = (error: unknown): string | undefined => {
  const messages = (error as { response?: { data?: { messages?: unknown } } })?.response?.data?.messages;
  return Array.isArray(messages) && typeof messages[0] === 'string' ? messages[0] : undefined;
};

export async function updateCalendarSettingsByUserId(userId: string, data: CalendarSettings) {
  try {
    const newData: CalendarSettingsForm = {
      // weeklyHours is the source of truth and MUST ride along. This payload is
      // built field by field, so omitting it would send flat values only — and
      // the server treats a flat-only write as "regenerate per-day hours from
      // these", silently flattening a schedule that differs by day.
      weeklyHours: data.weeklyHours,
      daysOfWeek: data.daysOfWeek,
      endOfOperations: data.endOfOperations,
      standardDuration: data.standardDuration,
      startOfOperations: data.startOfOperations,
      delay: data.delay,
      applicationModeLink: data.applicationModeLink ?? undefined
    };

    await axiosInstance.patch(`/api/user/${userId}/calendar-settings`, newData);
  } catch (error) {
    console.error(error);
    throw new Error('Something went wrong while updating Calendar settings!');
  }
}

/**
 * Partial update of a user's calendar settings — sends ONLY the keys given.
 *
 * Two screens now edit this one row: the AI Assistant tab's Application Link
 * and the Scheduling panel's availability card. updateCalendarSettingsByUserId
 * always sends all six fields, so whichever screen saved second would overwrite
 * the other's change with its own stale snapshot. It also maps null to
 * undefined, which is why a cleared Application Link never reaches the API —
 * an explicit null is preserved here so clearing a field actually clears it.
 */
export async function patchCalendarSettingsByUserId(userId: string, patch: Partial<CalendarSettings>): Promise<void> {
  try {
    // The callers show the error themselves (the application link, inline).
    await axiosInstance.patch(`/api/user/${userId}/calendar-settings`, patch, { showsOwnError: true });
  } catch (error) {
    console.error(error);
    throw new Error(apiErrorMessage(error) ?? 'Something went wrong while updating Calendar settings!');
  }
}

export async function getCalendarCategoriesList(queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      params: queryParams
    };

    const { data } = await axiosInstance.get<CalendarCategory[]>('/api/calendar/category/list', config);
    return data;
  } catch (error) {
    console.error(error);
    throw new Error('Something went wrong while retrieving Calendar categories!');
  }
}

type UpdateCalendarCategory = {
  name?: string;
  color?: string;
};

export async function updateCalendarCategory(id: string, data: UpdateCalendarCategory) {
  try {
    await axiosInstance.put(`/api/calendar/category/${id}`, data);
  } catch (error) {
    console.error(error);
    throw new Error('Something went wrong while updating Calendar category!');
  }
}

export async function createCalendarCategory(name: string, color: string): Promise<CustomField> {
  try {
    const newCategory = { name, color };

    const { data } = await axiosInstance.post<CustomField>('/api/calendar/category', newCategory);
    return data;
  } catch (error) {
    console.error(error);
    throw new Error('Something went wrong while creating Calendar Category!');
  }
}

export async function deleteCalendarCategory(id: string) {
  try {
    await axiosInstance.delete(`/api/calendar/category/${id}`);
  } catch (error) {
    console.error(error);
    throw new Error('Something went wrong while deleting Calendar Category!');
  }
}

export async function getTimeOffByUserId(userId: string): Promise<UserTimeOff[]> {
  try {
    const { data } = await axiosInstance.get<UserTimeOff[]>(`/api/user/${userId}/time-off`);
    return data;
  } catch (error) {
    console.error(error);
    throw new Error('Something went wrong while retrieving time off!');
  }
}

export async function createTimeOff(userId: string, payload: CreateUserTimeOff): Promise<UserTimeOff> {
  // Errors are rethrown with the server's own message: the useful failures here
  // are "overlaps time off you already have" and "more than a year", and a
  // generic string would hide exactly what the user needs to fix.
  const { data } = await axiosInstance.post<UserTimeOff>(`/api/user/${userId}/time-off`, payload);
  return data;
}

export async function deleteTimeOff(userId: string, id: string): Promise<void> {
  await axiosInstance.delete(`/api/user/${userId}/time-off/${id}`);
}
